import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, Users, CheckCircle, XCircle } from 'lucide-react';
import { schedulesAPI, bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function StudentSchedulePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Get studio ID from user
  const studioId = user?.studioId;

  // Fetch schedules for the studio
  const { data: schedulesData, isLoading } = useQuery({
    queryKey: ['schedules', studioId],
    queryFn: () => schedulesAPI.getSchedules(studioId),
    enabled: !!studioId,
    retry: false
  });

  // Fetch student's bookings
  const { data: myBookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false
  });

  const allSchedules = schedulesData?.data?.schedules || [];
  const myBookings = myBookingsData?.data || [];

  // Filter schedules to only show relevant ones
  const schedules = allSchedules.filter(schedule => {
    // If it has a specificDate (one-time), only show if it's today or in the future
    if (schedule.specificDate) {
      const scheduleDate = new Date(schedule.specificDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return scheduleDate >= today;
    }
    // If no specificDate, it's recurring - always show
    return true;
  });

  // Create a map of schedule IDs to booking status
  const bookingMap = {};
  myBookings.forEach(booking => {
    if (booking.status === 'confirmed') {
      bookingMap[booking.scheduleId] = booking.id;
    }
  });

  // Create booking mutation
  const createBookingMutation = useMutation({
    mutationFn: (data) => bookingsAPI.createBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-bookings']);
      queryClient.invalidateQueries(['schedules']);
      toast.success('Class booked successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to book class');
    }
  });

  // Cancel booking mutation
  const cancelBookingMutation = useMutation({
    mutationFn: (data) => bookingsAPI.cancelBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-bookings']);
      queryClient.invalidateQueries(['schedules']);
      toast.success('Booking cancelled');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel booking');
    }
  });

  const handleBookClass = (scheduleId) => {
    createBookingMutation.mutate({
      scheduleId,
      studentId: user.id
    });
  };

  const handleCancelBooking = (bookingId) => {
    cancelBookingMutation.mutate({
      bookingId,
      studentId: user.id
    });
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek] || 'Unknown';
  };

  const formatTime = (timeString) => {
    // Convert HH:MM:SS to HH:MM
    return timeString ? timeString.substring(0, 5) : '';
  };

  const getScheduleType = (schedule) => {
    console.log('Schedule data:', {
      id: schedule.id,
      specificDate: schedule.specificDate,
      dayOfWeek: schedule.dayOfWeek,
      recurrenceType: schedule.recurrenceType
    });

    // If has specificDate, it's a one-time schedule
    if (schedule.specificDate) {
      const date = new Date(schedule.specificDate);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    // If has dayOfWeek but no specificDate, it's recurring
    if (schedule.dayOfWeek !== null) {
      return `Every ${getDayName(schedule.dayOfWeek)}`;
    }
    return 'One-time';
  };

  const isFull = (schedule) => {
    const current = schedule.current_bookings || 0;
    const max = schedule.max_capacity || 10;
    return current >= max;
  };

  const isBooked = (scheduleId) => {
    return bookingMap[scheduleId] !== undefined;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Class Schedule</h1>
        <p className="text-gray-600 mt-2">Book your classes</p>
      </div>

      {schedules.length === 0 ? (
        <div className="card text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No classes available</h3>
          <p className="text-gray-600">Check back later for scheduled classes</p>
        </div>
      ) : (
        <div className="space-y-4">
          {schedules.map(schedule => {
            const booked = isBooked(schedule.id);
            const full = isFull(schedule);
            const available = (schedule.max_capacity || 10) - (schedule.current_bookings || 0);

            return (
              <div key={schedule.id} className="card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {schedule.video?.title || 'Untitled Class'}
                      </h3>
                      {booked && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          Booked
                        </span>
                      )}
                      {full && !booked && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                          Full
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{getScheduleType(schedule)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(schedule.startTime)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>
                          {available} / {schedule.max_capacity || 10} spots available
                        </span>
                      </div>
                      {schedule.video?.instructor && (
                        <div className="text-gray-600 text-sm">
                          Instructor: {schedule.video.instructor.firstName} {schedule.video.instructor.lastName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="ml-4">
                    {booked ? (
                      <button
                        onClick={() => handleCancelBooking(bookingMap[schedule.id])}
                        disabled={cancelBookingMutation.isPending}
                        className="btn btn-outline text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Cancel Booking
                      </button>
                    ) : full ? (
                      <button
                        disabled
                        className="btn btn-outline opacity-50 cursor-not-allowed"
                      >
                        Class Full
                      </button>
                    ) : (
                      <button
                        onClick={() => handleBookClass(schedule.id)}
                        disabled={createBookingMutation.isPending}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Book Slot
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}





